import { useState } from 'react';
import type { ConversationItem } from '@vivport/backend/types';
import { useNavigateWithContentFlush } from '@/hooks';
import { getContentTypeIcon, getContentTypeDisplayLabel } from '@/lib/content-labels';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

type ContentReadCardItem = Extract<ConversationItem, { kind: 'content_read_card' }>;

export interface ProvenanceContentReadCardProps {
  item: ContentReadCardItem;
}

export function ProvenanceContentReadCard({ item }: ProvenanceContentReadCardProps) {
  const navigate = useNavigateWithContentFlush();
  const [expanded, setExpanded] = useState(false);
  const HeaderIcon = getContentTypeIcon(item.contentType);
  const title = item.title && item.title.trim().length > 0 ? item.title : '(untitled)';
  const typeLabel = getContentTypeDisplayLabel(item.contentType);

  return (
    <div className="w-full rounded-lg border border-border p-3 text-sm text-foreground">
      <div className="flex items-center gap-2">
        <HeaderIcon className="h-4 w-4 shrink-0" />
        <p className="min-w-0 truncate font-medium">{title}</p>
        <button
          type="button"
          onClick={() => navigate(item.url)}
          className="ml-auto rounded p-1 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
          aria-label="Open content"
          title="Open content"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
        <span className="shrink-0">{typeLabel}</span>
        {item.readingStatsLine ? (
          <>
            <span className="shrink-0">·</span>
            <span className="min-w-0 truncate">{item.readingStatsLine}</span>
          </>
        ) : null}
      </div>
      <p className="mt-1 text-sm text-foreground">{item.spokenSummary}</p>
      {item.spokenText !== null ? (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setExpanded((p) => !p)}
            className="inline-flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
            )}
            <span>{`Read aloud · ${item.segmentLabel ?? 'segment'}${item.truncated ? ' · truncated' : ''}`}</span>
          </button>
          {expanded ? (
            <div className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">{item.spokenText}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
